import React from 'react';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { withTranslation, WithTranslation } from 'react-i18next';
// Ressources
import HtmlUtils from '../../utils/htmlUtils';

// How long the button stays on its "copied" state
const FEEDBACK_DURATION = 1500;

interface CopyButtonProps extends WithTranslation {
    value?: string | number | null; // What goes in the clipboard
    label?: string; // What is being copied, to make the tooltip explicit
    style?: React.CSSProperties;
}

/**
 * Copy a value to the clipboard and confirm it on the button itself.
 * Renders nothing when there is nothing to copy, so it can be dropped next to
 * any label without having to check the value first.
 */
const CopyButton = ({ t, value, label, style }: CopyButtonProps): JSX.Element => {
    const classes = useStyles();
    const [copied, setCopied] = React.useState<boolean>(false);
    const timeout = React.useRef<number>(null);

    // Avoid setting the state back on an unmounted button
    React.useEffect(
        () => () => {
            if (timeout.current) {
                window.clearTimeout(timeout.current);
            }
        },
        [],
    );

    const text = value === null || value === undefined ? '' : String(value).trim();

    const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        // The button can sit inside a link or a clickable row
        event.preventDefault();
        event.stopPropagation();

        if (!(await HtmlUtils.copyToClipboard(text))) {
            return;
        }

        setCopied(true);
        if (timeout.current) {
            window.clearTimeout(timeout.current);
        }
        timeout.current = window.setTimeout(() => setCopied(false), FEEDBACK_DURATION);
    };

    if (!text) {
        return null;
    }

    const title = copied ? t('copy.done') : label ? t('copy.action_label', { label }) : t('copy.action');

    return (
        <Tooltip title={title}>
            <IconButton size="small" className={classes.button} style={style} aria-label={title} onClick={handleCopy}>
                {copied ? (
                    <CheckIcon fontSize="inherit" className={classes.copied} />
                ) : (
                    <FileCopyOutlinedIcon fontSize="inherit" />
                )}
            </IconButton>
        </Tooltip>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        button: {
            marginLeft: theme.spacing(0.5),
            padding: theme.spacing(0.5),
            fontSize: 14,
            color: theme.palette.colors.black,
        },
        copied: {
            color: theme.palette.success.main,
        },
    }),
);

export default withTranslation(['common'])(CopyButton);
